import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import DOMPurify from "dompurify";
import {
  Spinner,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import {
  getMantisConversations,
  getMantisConversationMessagesByConversationId,
  getMantisConversationSessionsByConversationId,
} from "../ApiCalls/mantisConversations";
import { getMantisAgents } from "../ApiCalls/mantisAgents";
import { useBatchAIAnalysis } from "../hooks/useBatchAIAnalysis";
import { analyzeConversation } from "../utils/conversationAnalysis";
import * as XLSX from "xlsx";

// Message HTML reaches this page from outside the trust boundary — inbound
// email bodies and anything written to MantisConversationMessage.content.html
// through the API. Rendering it raw let a message sender run script in a
// backoffice admin's session, which is where the admin JWT lives.
const sanitizeMessageHtml = (html) =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "form", "input", "button"],
    FORBID_ATTR: ["style", "formaction", "target"],
  });

const select = (state) => {
  return {
    userId: state.auth.userId,
    token: state.auth.token,
  };
};

const Conversations = (props) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Conversation details modal
  const MESSAGES_PAGE_SIZE = 100;
  const [conversationModal, setConversationModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [, setConversationSessions] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    agentId: "",
  });
  const [agents, setAgents] = useState([]);

  // Pagination
  const CONVERSATIONS_PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [totalConversationsCount, setTotalConversationsCount] = useState(0);

  useEffect(() => {
    if (props.token) {
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchConversations is redefined each render; adding it would re-run this effect on every render
  }, [props.token, filters, page]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on filters change
  }, [filters]);

  // Agents for the "Agente" filter dropdown
  useEffect(() => {
    if (!props.token) return;
    const fetchAgents = async () => {
      try {
        const data = await getMantisAgents(props.token);
        const list = data.data || [];
        setAgents(
          [...list].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", "es")
          )
        );
      } catch (err) {
        console.error("Error fetching agents:", err);
      }
    };
    fetchAgents();
  }, [props.token]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      );
      filterParams.page = page;
      filterParams.limit = CONVERSATIONS_PAGE_SIZE;

      const data = await getMantisConversations(props.token, filterParams);
      setConversations(data.data || []);
      setTotalConversationsCount(
        typeof data.total === "number" ? data.total : (data.data || []).length
      );
    } catch (err) {
      setError("Error al cargar las conversaciones");
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetches every conversation matching the current filters (not just the
  // current page) for bulk operations like export and AI batch analysis.
  const fetchAllConversationsMatchingFilters = async () => {
    const filterParams = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== "")
    );
    const bulkPageSize = 200;
    let bulkPage = 1;
    let all = [];
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const data = await getMantisConversations(props.token, {
        ...filterParams,
        page: bulkPage,
        limit: bulkPageSize,
      });
      const batch = data.data || [];
      all = all.concat(batch);
      if (batch.length < bulkPageSize) break;
      bulkPage += 1;
    }
    return all;
  };

  const fetchConversationDetails = async (conversationId) => {
    try {
      setMessagesLoading(true);

      const [messagesData, sessionsData] = await Promise.all([
        getMantisConversationMessagesByConversationId(
          props.token,
          conversationId,
          MESSAGES_PAGE_SIZE,
          0
        ),
        getMantisConversationSessionsByConversationId(
          props.token,
          conversationId
        ),
      ]);

      const messages = messagesData.data || [];
      setConversationMessages(messages);
      setMessagesOffset(messages.length);
      setHasMoreMessages(messages.length === MESSAGES_PAGE_SIZE);
      setConversationSessions(sessionsData.data || []);
    } catch (err) {
      console.error("Error fetching conversation details:", err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadMoreMessages = async () => {
    if (!selectedConversation || loadingMoreMessages) return;
    try {
      setLoadingMoreMessages(true);
      const messagesData = await getMantisConversationMessagesByConversationId(
        props.token,
        selectedConversation._id,
        MESSAGES_PAGE_SIZE,
        messagesOffset
      );
      const nextMessages = messagesData.data || [];
      setConversationMessages((prev) => [...prev, ...nextMessages]);
      setMessagesOffset((prev) => prev + nextMessages.length);
      setHasMoreMessages(nextMessages.length === MESSAGES_PAGE_SIZE);
    } catch (err) {
      console.error("Error loading more messages:", err);
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  const openConversationModal = (conversation) => {
    setSelectedConversation(conversation);
    setConversationModal(true);
    fetchConversationDetails(conversation._id);
  };

  const closeConversationModal = () => {
    setConversationModal(false);
    setSelectedConversation(null);
    setConversationMessages([]);
    setConversationSessions([]);
    setMessagesOffset(0);
    setHasMoreMessages(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      agentId: "",
    });
  };

  const [exportLoading, setExportLoading] = useState(false);

  // AI Analysis configuration and state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisConfig, setAnalysisConfig] = useState({
    model: ["us.anthropic.claude-haiku-4-5-20251001-v1:0"],
    prompt: "",
    outputColumns: [
      { key: "conversationId", label: "ID de Conversación", enabled: true },
      { key: "contactName", label: "Nombre del Contacto", enabled: true },
      { key: "contactEmail", label: "Email del Contacto", enabled: true },
      { key: "contactPhone", label: "Teléfono del Contacto", enabled: true },
      { key: "contactCompany", label: "Empresa del Contacto", enabled: true },
      { key: "status", label: "Estado", enabled: true },
      { key: "priority", label: "Prioridad", enabled: true },
      { key: "assignedAgent", label: "Agente Asignado", enabled: true },
      {
        key: "analysisData",
        label: "Datos del Análisis (JSON)",
        enabled: true,
      },
      { key: "totalMessages", label: "Total de Mensajes", enabled: true },
      { key: "analysisDate", label: "Fecha de Análisis", enabled: true },
      { key: "dateCreated", label: "Fecha de Creación", enabled: true },
    ],
  });

  // Create the analysis function
  const performAnalysis = useCallback(
    async (conversation, messages) => {
      return await analyzeConversation(
        conversation,
        messages,
        analysisConfig.prompt,
        {
          model: analysisConfig.model,
          token: props.token,
        }
      );
    },
    [analysisConfig.prompt, analysisConfig.model, props.token]
  );

  // Create message fetcher function
  const getMessagesForConversation = useCallback(
    async (conversationId) => {
      try {
        const messagesData =
          await getMantisConversationMessagesByConversationId(
            props.token,
            conversationId,
            1000, // Get up to 1000 messages
            0
          );
        return messagesData?.data || messagesData || [];
      } catch (error) {
        console.error(
          `Error fetching messages for conversation ${conversationId}:`,
          error
        );
        return [];
      }
    },
    [props.token]
  );

  // Use the batch analysis hook
  const {
    processBatch,
    isProcessing: isAnalyzing,
    progress: analysisProgress,
    results: batchResults,
    errors: batchErrors,
    isPaused: isAnalysisPaused,
    pause: pauseAnalysis,
    resume: resumeAnalysis,
    cancel: cancelAnalysis,
    reset: resetAnalysis,
  } = useBatchAIAnalysis(performAnalysis, {
    batchSize: 1,
    delayBetweenBatches: 2000, // 2 seconds between calls to avoid rate limits
    onProgress: (progress, results) => {
      console.log(
        `Progress: ${progress.percentage}% (${progress.current}/${progress.total})`
      );
    },
    onComplete: (results, errors) => {
      console.log(
        "Analysis complete:",
        results.length,
        "successful,",
        errors.length,
        "errors"
      );
    },
    onError: (error, conversation, index) => {
      console.error(`Error at index ${index}:`, error);
    },
  });

  // Function to start analysis
  const startAnalysis = async () => {
    if (totalConversationsCount === 0) {
      alert("No hay conversaciones para analizar");
      return;
    }

    if (!analysisConfig.prompt.trim()) {
      alert("Por favor, configura el prompt antes de iniciar el análisis");
      setShowConfigModal(true);
      return;
    }

    setShowConfigModal(false);
    setShowAnalysisModal(true);
    resetAnalysis();

    try {
      const analysisConversations = await fetchAllConversationsMatchingFilters();
      await processBatch(analysisConversations, getMessagesForConversation);
    } catch (error) {
      console.error("Error in batch analysis:", error);
      alert("Error al iniciar el análisis. Por favor, intenta de nuevo.");
    }
  };

  // Function to export analysis results to Excel
  const exportAnalysisToExcel = async (resultsToExport = null) => {
    const results = resultsToExport || batchResults || [];

    if (results.length === 0) {
      alert("No hay resultados para exportar");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // Get enabled columns
      const enabledColumns = analysisConfig.outputColumns.filter(
        (col) => col.enabled
      );

      // Check if analysisData column is enabled
      const includeAnalysisDataColumn = enabledColumns.some(
        (col) => col.key === "analysisData"
      );

      // Filter out analysisData from standard columns processing (we'll handle it separately)
      const standardColumns = enabledColumns.filter(
        (col) => col.key !== "analysisData"
      );

      // Collect all unique keys from analysis responses to create dynamic columns
      const analysisKeysSet = new Set();
      results.forEach((result) => {
        const analysis = result.analysis?.data || result.analysis || {};
        if (
          analysis &&
          typeof analysis === "object" &&
          !Array.isArray(analysis)
        ) {
          Object.keys(analysis).forEach((key) => {
            analysisKeysSet.add(key);
          });
        }
      });

      // Convert set to array and create column definitions for analysis keys
      const analysisKeys = Array.from(analysisKeysSet);
      const analysisColumns = analysisKeys.map((key) => ({
        key: key,
        label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
        enabled: true,
      }));

      // Combine standard columns with analysis columns
      const allColumns = [...standardColumns, ...analysisColumns];

      // Add analysisData column if enabled
      if (includeAnalysisDataColumn) {
        const analysisDataColumn = enabledColumns.find(
          (col) => col.key === "analysisData"
        );
        if (analysisDataColumn) {
          allColumns.push(analysisDataColumn);
        }
      }

      // Prepare data for export
      const analysisData = results.map((result) => {
        const conv = result.conversation;
        const analysis = result.analysis?.data || result.analysis || {};
        const row = {};

        // Process standard columns
        standardColumns.forEach((col) => {
          switch (col.key) {
            case "conversationId":
              row[col.label] = result.conversationId;
              break;
            case "contactName":
              row[col.label] = conv.contact?.name || "Sin nombre";
              break;
            case "contactEmail":
              row[col.label] = conv.contact?.email || "N/A";
              break;
            case "contactPhone":
              row[col.label] = conv.contact?.phoneNumber || "N/A";
              break;
            case "contactCompany":
              row[col.label] = conv.contact?.company || "N/A";
              break;
            case "status":
              row[col.label] = conv.status || "N/A";
              break;
            case "priority":
              row[col.label] = conv.priority || "NORMAL";
              break;
            case "assignedAgent":
              row[col.label] = conv.assignedAgentId?.name || "Sin asignar";
              break;
            case "totalMessages":
              row[col.label] = result.messages?.length || 0;
              break;
            case "analysisDate":
              row[col.label] = result.processedAt
                ? formatDate(result.processedAt)
                : "N/A";
              break;
            case "dateCreated":
              row[col.label] = conv.dateCreated
                ? formatDate(conv.dateCreated)
                : "N/A";
              break;
            default:
              // For custom columns, try to get from analysis data
              row[col.label] = analysis[col.key] || "N/A";
          }
        });

        // Add all analysis response fields as separate columns
        analysisKeys.forEach((key) => {
          const value = analysis[key];
          // Format the value appropriately
          if (value === null || value === undefined) {
            row[key.charAt(0).toUpperCase() + key.slice(1)] = "N/A";
          } else if (typeof value === "object") {
            // If it's an object, stringify it
            row[key.charAt(0).toUpperCase() + key.slice(1)] =
              JSON.stringify(value);
          } else {
            row[key.charAt(0).toUpperCase() + key.slice(1)] = String(value);
          }
        });

        // Add the full JSON analysis data column if enabled
        if (includeAnalysisDataColumn) {
          const analysisDataColumn = enabledColumns.find(
            (col) => col.key === "analysisData"
          );
          if (analysisDataColumn) {
            row[analysisDataColumn.label] = JSON.stringify(analysis);
          }
        }

        return row;
      });

      const ws = XLSX.utils.json_to_sheet(analysisData);

      // Set column widths
      const colWidths = allColumns.map((col) => {
        if (col.key === "contactEmail") return { wch: 30 };
        if (col.key === "conversationId") return { wch: 25 };
        if (col.key === "analysisData") return { wch: 50 }; // Wide column for JSON
        // Wider columns for analysis data that might be longer
        if (analysisKeys.includes(col.key)) {
          // Check if any value in this column is long
          const maxLength = Math.max(
            ...analysisData.map((row) => {
              const val = row[col.label];
              return val ? String(val).length : 0;
            })
          );
          return { wch: Math.min(Math.max(maxLength / 2, 15), 50) };
        }
        return { wch: 20 };
      });
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Análisis de Conversaciones");

      // Add summary sheet if we have analysis data
      const summaryData = [
        { Métrica: "Total Analizado", Valor: results.length },
        { Métrica: "Exitosos", Valor: results.filter((r) => r.success).length },
        { Métrica: "Con Errores", Valor: batchErrors.length },
      ];

      // Try to extract categories from analysis data
      const categoryCounts = {};
      results.forEach((result) => {
        const analysis = result.analysis?.data || result.analysis || {};
        if (analysis.categoria) {
          categoryCounts[analysis.categoria] =
            (categoryCounts[analysis.categoria] || 0) + 1;
        }
      });

      if (Object.keys(categoryCounts).length > 0) {
        summaryData.push({ Métrica: "", Valor: "" });
        summaryData.push({ Métrica: "Distribución por Categoría", Valor: "" });
        Object.entries(categoryCounts).forEach(([categoria, count]) => {
          summaryData.push({ Métrica: categoria, Valor: count });
        });
      }

      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      summaryWs["!cols"] = [{ wch: 30 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, "Resumen");

      // Generate filename
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `analisis_conversaciones_${currentDate}.xlsx`;

      XLSX.writeFile(wb, filename);
      alert(`Análisis exportado exitosamente: ${filename}`);
    } catch (error) {
      console.error("Error exporting analysis:", error);
      alert(`Error al exportar: ${error.message}`);
    }
  };

  // Handle column configuration change
  const handleColumnToggle = (index) => {
    const newColumns = [...analysisConfig.outputColumns];
    newColumns[index].enabled = !newColumns[index].enabled;
    setAnalysisConfig({ ...analysisConfig, outputColumns: newColumns });
  };

  // Add custom column
  const handleAddCustomColumn = () => {
    const label = prompt("Ingresa el nombre de la columna:");
    if (label && label.trim()) {
      const key = prompt(
        "Ingresa la clave (key) para acceder al dato del análisis (ej: categoria, explicacion):"
      );
      if (key && key.trim()) {
        const newColumns = [
          ...analysisConfig.outputColumns,
          {
            key: key.trim(),
            label: label.trim(),
            enabled: true,
          },
        ];
        setAnalysisConfig({ ...analysisConfig, outputColumns: newColumns });
      }
    }
  };

  // Remove custom column
  const handleRemoveCustomColumn = (index) => {
    const newColumns = analysisConfig.outputColumns.filter(
      (_, i) => i !== index
    );
    setAnalysisConfig({ ...analysisConfig, outputColumns: newColumns });
  };

  // Excel cell character limit (32767 characters)
  const EXCEL_MAX_CELL_LENGTH = 32767;

  // Helper function to truncate text to Excel's maximum cell length
  const truncateForExcel = (text, suffix = "") => {
    if (!text || typeof text !== "string") return text || "";
    if (text.length <= EXCEL_MAX_CELL_LENGTH) return text;

    const maxLength = EXCEL_MAX_CELL_LENGTH - suffix.length;
    return text.substring(0, maxLength) + suffix;
  };

  const getContextKeys = (list) => {
    const keySet = new Set();
    (list || []).forEach((c) => {
      const ctx = c.context;
      if (ctx && typeof ctx === "object") Object.keys(ctx).forEach((k) => keySet.add(k));
    });
    return [...keySet].sort();
  };

  const formatContextValue = (val) => {
    if (val === undefined || val === null) return "N/A";
    if (Array.isArray(val)) {
      const str = val.every((x) => typeof x !== "object" || x === null)
        ? val.map(String).join(", ")
        : JSON.stringify(val);
      return truncateForExcel(str);
    }
    if (typeof val === "object") return truncateForExcel(JSON.stringify(val));
    return String(val);
  };

  const exportToExcel = async () => {
    if (totalConversationsCount === 0) {
      alert("No hay conversaciones para exportar");
      return;
    }

    setExportLoading(true);

    try {
      // Export the full filtered result set, not just the current page
      const conversations = await fetchAllConversationsMatchingFilters();

      if (conversations.length === 0) {
        alert("No hay conversaciones para exportar");
        return;
      }

      // Create workbook
      const wb = XLSX.utils.book_new();

      const contextKeys = getContextKeys(conversations);

      // 1. Main Conversations Worksheet
      const conversationsData = conversations.map((conversation) => {
        const base = {
          "ID de Conversación": conversation._id,
          "Nombre del Contacto": conversation.contact?.name || "Sin nombre",
          "Email del Contacto": conversation.contact?.email || "N/A",
          "Teléfono del Contacto": conversation.contact?.phoneNumber || "N/A",
          "Empresa del Contacto": conversation.contact?.company || "N/A",
          Estado: conversation.status || "N/A",
          Prioridad: conversation.priority || "NORMAL",
          "Agente Asignado": conversation.assignedAgentId?.name || "Sin asignar",
          "ID del Agente": conversation.assignedAgentId?.agentId || "N/A",
          Canales:
            conversation.channels?.map((ch) => ch.type).join(", ") || "N/A",
          "Fecha de Creación": conversation.dateCreated
            ? formatDate(conversation.dateCreated)
            : "N/A",
          "Fecha de Actualización": conversation.dateUpdated
            ? formatDate(conversation.dateUpdated)
            : "N/A",
          Tags: truncateForExcel(conversation.tags?.join(", ") || "N/A"),
          "ID de Negocio": conversation.mantisBusinessId || "N/A",
          "ID de Usuario": conversation.userId || "N/A",
        };
        const ctx = conversation.context || {};
        contextKeys.forEach((k) => {
          base[`Context - ${k}`] = formatContextValue(ctx[k]);
        });
        return base;
      });

      const conversationsWs = XLSX.utils.json_to_sheet(conversationsData);

      const conversationsColWidths = [
        { wch: 25 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 },
        { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 30 },
        { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
        ...contextKeys.map(() => ({ wch: 25 })),
      ];
      conversationsWs["!cols"] = conversationsColWidths;
      XLSX.utils.book_append_sheet(wb, conversationsWs, "Conversaciones");

      // 2. Messages Worksheet - Simplified conversation view
      const conversationMessagesData = [];

      // We need to fetch messages for each conversation
      // Since this is async, we'll create a promise to handle this
      const fetchAllMessages = async () => {
        try {
          const messagePromises = conversations.map(async (conversation) => {
            try {
              const messagesData =
                await getMantisConversationMessagesByConversationId(
                  props.token,
                  conversation._id,
                  1000, // Get up to 1000 messages per conversation
                  0
                );
              return {
                conversationId: conversation._id,
                conversationName: conversation.contact?.name || "Sin nombre",
                messages: messagesData?.data || messagesData || [],
              };
            } catch (error) {
              console.error(
                `Error fetching messages for conversation ${conversation._id}:`,
                error
              );
              return {
                conversationId: conversation._id,
                conversationName: conversation.contact?.name || "Sin nombre",
                messages: [],
              };
            }
          });

          const conversationMessages = await Promise.all(messagePromises);

          conversationMessages.forEach(
            ({ conversationId, conversationName, messages }) => {
              try {
                // Sort messages by date to show chronological order
                const sortedMessages = (messages || []).sort((a, b) => {
                  const dateA = a.dateCreated
                    ? new Date(a.dateCreated).getTime()
                    : 0;
                  const dateB = b.dateCreated
                    ? new Date(b.dateCreated).getTime()
                    : 0;
                  return dateA - dateB;
                });

                // Format the conversation as a readable text
                let conversationText = sortedMessages
                  .map((message, index) => {
                    const sender =
                      message.sender === "USER"
                        ? "Usuario"
                        : message.sender === "AGENT"
                        ? "Agente"
                        : message.sender === "MANTIS"
                        ? "Mantis"
                        : message.sender || "Desconocido";

                    const timestamp = message.dateCreated
                      ? formatDate(message.dateCreated)
                      : "Sin fecha";
                    let content =
                      message.content?.text ||
                      message.content?.html ||
                      "Sin contenido";

                    // Truncate individual message content if too long (leave room for message header)
                    const messageHeader = `${
                      index + 1
                    }. [${sender}] (${timestamp}): `;
                    const maxContentLength =
                      EXCEL_MAX_CELL_LENGTH - messageHeader.length - 50; // Extra buffer
                    if (content.length > maxContentLength) {
                      content =
                        content.substring(0, maxContentLength) +
                        "... [contenido truncado]";
                    }

                    return `${messageHeader}${content}`;
                  })
                  .join("\n\n");

                // Truncate conversation text if it exceeds Excel's cell limit
                const truncationNote =
                  "\n\n[... La conversación fue truncada debido a la longitud máxima de Excel (32767 caracteres) ...]";
                conversationText = truncateForExcel(
                  conversationText || "Sin mensajes",
                  truncationNote
                );

                // Find the conversation object to get contact info
                const conversation = conversations.find(
                  (c) => c._id === conversationId
                );

                const ctx = conversation?.context || {};
                const msgRow = {
                  "ID de Conversación": conversationId,
                  "Nombre de Conversación": conversationName,
                  "Email del Contacto": conversation?.contact?.email || "N/A",
                  "Teléfono del Contacto":
                    conversation?.contact?.phoneNumber || "N/A",
                  "Empresa del Contacto":
                    conversation?.contact?.company || "N/A",
                  "Estado de Conversación": conversation?.status || "N/A",
                  Prioridad: conversation?.priority || "NORMAL",
                  "Agente Asignado":
                    conversation?.assignedAgentId?.name || "Sin asignar",
                  Canales:
                    conversation?.channels?.map((ch) => ch.type).join(", ") ||
                    "N/A",
                  "Conversación Completa": conversationText || "Sin mensajes",
                  "Total de Mensajes": sortedMessages.length,
                  "Primer Mensaje":
                    sortedMessages.length > 0
                      ? formatDate(sortedMessages[0].dateCreated)
                      : "N/A",
                  "Último Mensaje":
                    sortedMessages.length > 0
                      ? formatDate(
                          sortedMessages[sortedMessages.length - 1].dateCreated
                        )
                      : "N/A",
                  "Fecha de Creación": conversation?.dateCreated
                    ? formatDate(conversation.dateCreated)
                    : "N/A",
                  "Fecha de Actualización": conversation?.dateUpdated
                    ? formatDate(conversation.dateUpdated)
                    : "N/A",
                };
                contextKeys.forEach((k) => {
                  msgRow[`Context - ${k}`] = formatContextValue(ctx[k]);
                });
                conversationMessagesData.push(msgRow);
              } catch (error) {
                console.error(
                  `Error processing messages for conversation ${conversationId}:`,
                  error
                );
                const conversation = conversations.find(
                  (c) => c._id === conversationId
                );
                const ctx = conversation?.context || {};
                const errRow = {
                  "ID de Conversación": conversationId,
                  "Nombre de Conversación": conversationName,
                  "Email del Contacto": conversation?.contact?.email || "N/A",
                  "Teléfono del Contacto":
                    conversation?.contact?.phoneNumber || "N/A",
                  "Empresa del Contacto":
                    conversation?.contact?.company || "N/A",
                  "Estado de Conversación": conversation?.status || "N/A",
                  Prioridad: conversation?.priority || "NORMAL",
                  "Agente Asignado":
                    conversation?.assignedAgentId?.name || "Sin asignar",
                  Canales:
                    conversation?.channels?.map((ch) => ch.type).join(", ") ||
                    "N/A",
                  "Conversación Completa": "Error al cargar mensajes",
                  "Total de Mensajes": 0,
                  "Primer Mensaje": "N/A",
                  "Último Mensaje": "N/A",
                  "Fecha de Creación": conversation?.dateCreated
                    ? formatDate(conversation.dateCreated)
                    : "N/A",
                  "Fecha de Actualización": conversation?.dateUpdated
                    ? formatDate(conversation.dateUpdated)
                    : "N/A",
                };
                contextKeys.forEach((k) => {
                  errRow[`Context - ${k}`] = formatContextValue(ctx[k]);
                });
                conversationMessagesData.push(errRow);
              }
            }
          );
        } catch (error) {
          console.error("Error in fetchAllMessages:", error);
          // Continue with export even if message fetching fails
        }
      };

      // Execute the async function and then create the messages worksheet
      await fetchAllMessages();

      if (conversationMessagesData.length > 0) {
        const messagesWs = XLSX.utils.json_to_sheet(conversationMessagesData);

        // Set column widths for messages
        const messagesColWidths = [
          { wch: 25 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 },
          { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 100 },
          { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
          ...contextKeys.map(() => ({ wch: 25 })),
        ];
        messagesWs["!cols"] = messagesColWidths;
        XLSX.utils.book_append_sheet(wb, messagesWs, "Mensajes");
      } else {
        // If no conversation messages data, create empty worksheet
        const emptyRow = {
          "ID de Conversación": "N/A",
          "Nombre de Conversación": "Sin datos",
          "Email del Contacto": "N/A",
          "Teléfono del Contacto": "N/A",
          "Empresa del Contacto": "N/A",
          "Estado de Conversación": "N/A",
          Prioridad: "N/A",
          "Agente Asignado": "N/A",
          Canales: "N/A",
          "Conversación Completa": "No se pudieron cargar los mensajes",
          "Total de Mensajes": 0,
          "Primer Mensaje": "N/A",
          "Último Mensaje": "N/A",
          "Fecha de Creación": "N/A",
          "Fecha de Actualización": "N/A",
        };
        contextKeys.forEach((k) => { emptyRow[`Context - ${k}`] = "N/A"; });
        const emptyMessagesData = [emptyRow];
        const emptyMessagesWs = XLSX.utils.json_to_sheet(emptyMessagesData);
        XLSX.utils.book_append_sheet(wb, emptyMessagesWs, "Mensajes");
      }

      // 3. Summary Worksheet
      const totalConversations = conversations.length;
      const activeConversations = conversations.filter(
        (c) => c.status === "ACTIVE"
      ).length;
      const pausedConversations = conversations.filter(
        (c) => c.status === "PAUSED"
      ).length;
      const closedConversations = conversations.filter(
        (c) => c.status === "CLOSED"
      ).length;
      const archivedConversations = conversations.filter(
        (c) => c.status === "ARCHIVED"
      ).length;

      const lowPriority = conversations.filter(
        (c) => c.priority === "LOW"
      ).length;
      const normalPriority = conversations.filter(
        (c) => c.priority === "NORMAL"
      ).length;
      const highPriority = conversations.filter(
        (c) => c.priority === "HIGH"
      ).length;
      const urgentPriority = conversations.filter(
        (c) => c.priority === "URGENT"
      ).length;

      const channelCounts = {};
      conversations.forEach((conversation) => {
        if (conversation.channels) {
          conversation.channels.forEach((channel) => {
            channelCounts[channel.type] =
              (channelCounts[channel.type] || 0) + 1;
          });
        }
      });

      const summaryData = [
        { Métrica: "Total de Conversaciones", Valor: totalConversations },
        { Métrica: "Conversaciones Activas", Valor: activeConversations },
        { Métrica: "Conversaciones Pausadas", Valor: pausedConversations },
        { Métrica: "Conversaciones Cerradas", Valor: closedConversations },
        { Métrica: "Conversaciones Archivadas", Valor: archivedConversations },
        { Métrica: "", Valor: "" },
        { Métrica: "Prioridad Baja", Valor: lowPriority },
        { Métrica: "Prioridad Normal", Valor: normalPriority },
        { Métrica: "Prioridad Alta", Valor: highPriority },
        { Métrica: "Prioridad Urgente", Valor: urgentPriority },
        { Métrica: "", Valor: "" },
        {
          Métrica: "Conversaciones con Agente Asignado",
          Valor: conversations.filter((c) => c.assignedAgentId).length,
        },
        {
          Métrica: "Conversaciones sin Agente Asignado",
          Valor: conversations.filter((c) => !c.assignedAgentId).length,
        },
        { Métrica: "", Valor: "" },
      ];

      // Add channel statistics
      Object.entries(channelCounts).forEach(([channel, count]) => {
        summaryData.push({ Métrica: `Canal ${channel}`, Valor: count });
      });

      // Add message statistics
      summaryData.push({ Métrica: "", Valor: "" });
      const totalMessages =
        conversationMessagesData.length > 0
          ? conversationMessagesData.reduce(
              (sum, conv) => sum + (conv["Total de Mensajes"] || 0),
              0
            )
          : 0;
      summaryData.push({ Métrica: "Total de Mensajes", Valor: totalMessages });

      const conversationsWithMessages =
        conversationMessagesData.length > 0
          ? conversationMessagesData.filter(
              (conv) => conv["Total de Mensajes"] > 0
            ).length
          : 0;
      const conversationsWithoutMessages =
        conversationMessagesData.length > 0
          ? conversationMessagesData.filter(
              (conv) => conv["Total de Mensajes"] === 0
            ).length
          : conversations.length;

      summaryData.push({
        Métrica: "Conversaciones con Mensajes",
        Valor: conversationsWithMessages,
      });
      summaryData.push({
        Métrica: "Conversaciones sin Mensajes",
        Valor: conversationsWithoutMessages,
      });

      const avgMessagesPerConversation =
        conversationsWithMessages > 0
          ? (totalMessages / conversationsWithMessages).toFixed(2)
          : 0;
      summaryData.push({
        Métrica: "Promedio de Mensajes por Conversación",
        Valor: avgMessagesPerConversation,
      });

      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      const summaryColWidths = [{ wch: 30 }, { wch: 15 }];
      summaryWs["!cols"] = summaryColWidths;
      XLSX.utils.book_append_sheet(wb, summaryWs, "Resumen");

      // Validate workbook has sheets before writing
      if (!wb.SheetNames || wb.SheetNames.length === 0) {
        throw new Error(
          "El libro de trabajo no tiene hojas. No se puede exportar."
        );
      }

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `conversaciones_mantis_${currentDate}.xlsx`;

      // Export file
      try {
        if (typeof XLSX.writeFile !== "function") {
          throw new Error(
            "XLSX.writeFile no está disponible. Verifica que la librería xlsx esté correctamente instalada."
          );
        }
        XLSX.writeFile(wb, filename);
        // Show success message
        alert(`Archivo exportado exitosamente: ${filename}`);
      } catch (writeError) {
        console.error("Error writing file:", writeError);
        alert(
          `Error al descargar el archivo: ${
            writeError.message || "Error desconocido"
          }. Por favor, verifica la consola para más detalles.`
        );
        throw writeError;
      }
    } catch (error) {
      console.error("Error exporting conversations:", error);
      alert(
        `Error al exportar las conversaciones: ${
          error.message || "Error desconocido"
        }. Por favor, verifica la consola para más detalles.`
      );
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      ACTIVE: "success",
      PAUSED: "warning",
      CLOSED: "secondary",
      ARCHIVED: "dark",
    };
    return colors[status] || "secondary";
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      LOW: "info",
      NORMAL: "primary",
      HIGH: "warning",
      URGENT: "danger",
    };
    return colors[priority] || "primary";
  };

  const getChannelIcon = (channel) => {
    const icons = {
      WHATSAPP: "fab fa-whatsapp",
      EMAIL: "fas fa-envelope",
      SMS: "fas fa-sms",
      CHAT: "fas fa-comments",
      TELEGRAM: "fab fa-telegram",
      SLACK: "fab fa-slack",
      WEBHOOK: "fas fa-link",
    };
    return icons[channel] || "fas fa-comment";
  };

  const getSenderIcon = (sender) => {
    const icons = {
      USER: "fas fa-user",
      AGENT: "fas fa-robot",
      MANTIS: "fas fa-bug",
    };
    return icons[sender] || "fas fa-user";
  };

  const getContactPhone = (conversation) =>
    conversation.contact?.phoneNumber ||
    conversation.channels?.find((channel) => channel.identifier)?.identifier ||
    "";

  // Most contacts reach us without a name, so fall back to the phone number
  // instead of showing "Sin nombre" on nearly every row.
  const getContactLabel = (conversation) =>
    conversation.contact?.name?.trim() ||
    getContactPhone(conversation) ||
    conversation.contact?.email ||
    "Sin nombre";

  const getAgentLabel = (conversation) => {
    const agent = conversation.assignedAgentId;
    if (!agent) return "";
    if (typeof agent === "string") return agent;
    return agent.name || agent.agentId || "";
  };

  const getMessageTypeIcon = (type) => {
    const icons = {
      TEXT: "fas fa-comment",
      IMAGE: "fas fa-image",
      AUDIO: "fas fa-volume-up",
      VIDEO: "fas fa-video",
      FILE: "fas fa-file",
      LOCATION: "fas fa-map-marker-alt",
      CONTACT: "fas fa-address-book",
      STICKER: "fas fa-smile",
      TEMPLATE: "fas fa-file-alt",
    };
    return icons[type] || "fas fa-comment";
  };

  return (
    <div className="conversations-page">
      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col xs={12} sm={6} md={3}>
          <FormGroup>
            <Label for="agentFilter">Agente</Label>
            <Input
              id="agentFilter"
              type="select"
              value={filters.agentId}
              onChange={(e) => handleFilterChange("agentId", e.target.value)}
            >
              <option value="">Todos</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.name || agent.agentId}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col
          xs={12}
          sm={12}
          md={9}
          className="d-flex flex-wrap align-items-end gap-2 mt-3 mt-md-0"
        >
          <Button
            color="secondary"
            onClick={clearFilters}
            className="flex-fill flex-sm-grow-0"
            style={{ minWidth: "140px" }}
          >
            <i className="fas fa-filter me-1"></i>
            Limpiar Filtros
          </Button>
          <Button
            color="info"
            onClick={() => setShowConfigModal(true)}
            disabled={totalConversationsCount === 0 || isAnalyzing}
            className="flex-fill flex-sm-grow-0"
            style={{ minWidth: "140px" }}
            title="Configurar y analizar conversaciones con IA"
          >
            <i className="fas fa-brain me-1"></i>
            Analizar con IA
          </Button>
          <Button
            color="success"
            onClick={exportToExcel}
            disabled={totalConversationsCount === 0 || exportLoading}
            className="flex-fill flex-sm-grow-0"
            style={{ minWidth: "140px" }}
            title={
              totalConversationsCount === 0
                ? "No hay conversaciones para exportar"
                : "Exportar conversaciones a Excel"
            }
          >
            {exportLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Exportando...
              </>
            ) : (
              <>
                <i className="fas fa-file-excel me-1"></i>
                Exportar Excel
              </>
            )}
          </Button>
          <Button
            color="primary"
            onClick={fetchConversations}
            className="flex-fill flex-sm-grow-0"
            style={{ minWidth: "140px" }}
          >
            <i className="fas fa-sync-alt me-1"></i>
            Actualizar
          </Button>
        </Col>
      </Row>

      {/* Conversations Table */}
      {loading ? (
        <div className="loading-container text-center">
          <Spinner color="primary" />
          <p>Cargando conversaciones...</p>
        </div>
      ) : error ? (
        <div className="error-container text-center">
          <p className="text-danger">{error}</p>
          <Button color="primary" onClick={fetchConversations}>
            Reintentar
          </Button>
        </div>
      ) : (
        <Row>
          <Col xs={12}>
            <Card className="conversations-card">
              <CardBody>
                <Table responsive className="conversations-table">
                  <thead>
                    <tr>
                      <th>Contacto</th>
                      <th>Canal</th>
                      <th>Estado</th>
                      <th>Prioridad</th>
                      <th>Agente Asignado</th>
                      <th>Última Actividad</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversations.length > 0 ? (
                      conversations.map((conversation) => {
                        const contactLabel = getContactLabel(conversation);
                        const contactPhone = getContactPhone(conversation);
                        const contactEmail = conversation.contact?.email;
                        const agentLabel = getAgentLabel(conversation);
                        return (
                          <tr key={conversation._id}>
                            <td>
                              <div>
                                <strong>{contactLabel}</strong>
                                {contactEmail && contactEmail !== contactLabel && (
                                  <div className="text-muted small">
                                    {contactEmail}
                                  </div>
                                )}
                                {contactPhone && contactPhone !== contactLabel && (
                                  <div className="text-muted small">
                                    {contactPhone}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              {conversation.channels &&
                              conversation.channels.length > 0 ? (
                                <div>
                                  {conversation.channels.map((channel, index) => (
                                    <Badge
                                      key={index}
                                      color="info"
                                      className="me-1"
                                    >
                                      <i
                                        className={`${getChannelIcon(
                                          channel.type
                                        )} me-1`}
                                      ></i>
                                      {channel.type}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>
                              <Badge
                                color={getStatusBadgeColor(conversation.status)}
                              >
                                {conversation.status || "N/A"}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                color={getPriorityBadgeColor(
                                  conversation.priority
                                )}
                              >
                                {conversation.priority || "NORMAL"}
                              </Badge>
                            </td>
                            <td>
                              {agentLabel ? (
                                <span>{agentLabel}</span>
                              ) : (
                                <span className="text-muted">Sin asignar</span>
                              )}
                            </td>
                            <td>
                              {formatDate(conversation.channels[0].lastActivity)}
                            </td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                  openConversationModal(conversation)
                                }
                              >
                                <i className="fas fa-eye me-1"></i>
                                Ver Detalles
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No hay conversaciones disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {totalConversationsCount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      Mostrando {(page - 1) * CONVERSATIONS_PAGE_SIZE + 1}-
                      {Math.min(
                        page * CONVERSATIONS_PAGE_SIZE,
                        totalConversationsCount
                      )}{" "}
                      de {totalConversationsCount} conversaciones
                    </small>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      >
                        Anterior
                      </Button>
                      <span className="text-muted small">
                        Página {page} de{" "}
                        {Math.max(
                          Math.ceil(totalConversationsCount / CONVERSATIONS_PAGE_SIZE),
                          1
                        )}
                      </span>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        disabled={
                          page * CONVERSATIONS_PAGE_SIZE >= totalConversationsCount
                        }
                        onClick={() => setPage((prev) => prev + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Conversation Details Modal */}
      <Modal
        isOpen={conversationModal}
        toggle={closeConversationModal}
        size="xl"
      >
        <ModalHeader toggle={closeConversationModal}>
          <i className="fas fa-comments me-2"></i>
          Detalles de la Conversación
        </ModalHeader>
        <ModalBody>
          {selectedConversation && (
            <div>
              {/* Conversation Info */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        <i className="fas fa-info-circle me-2 text-primary"></i>
                        Información del Contacto
                      </h5>
                      <div className="info-item mb-2">
                        <strong>Nombre:</strong>{" "}
                        {selectedConversation.contact?.name || "N/A"}
                      </div>
                      <div className="info-item mb-2">
                        <strong>Email:</strong>{" "}
                        {selectedConversation.contact?.email || "N/A"}
                      </div>
                      <div className="info-item mb-2">
                        <strong>Teléfono:</strong>{" "}
                        {selectedConversation.contact?.phoneNumber || "N/A"}
                      </div>
                      <div className="info-item mb-2">
                        <strong>Empresa:</strong>{" "}
                        {selectedConversation.contact?.company || "N/A"}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        <i className="fas fa-cogs me-2 text-primary"></i>
                        Estado de la Conversación
                      </h5>
                      <div className="info-item mb-2">
                        <strong>Estado:</strong>{" "}
                        <Badge
                          color={getStatusBadgeColor(
                            selectedConversation.status
                          )}
                        >
                          {selectedConversation.status}
                        </Badge>
                      </div>
                      <div className="info-item mb-2">
                        <strong>Prioridad:</strong>{" "}
                        <Badge
                          color={getPriorityBadgeColor(
                            selectedConversation.priority
                          )}
                        >
                          {selectedConversation.priority}
                        </Badge>
                      </div>
                      <div className="info-item mb-2">
                        <strong>Agente:</strong>{" "}
                        {getAgentLabel(selectedConversation) || "Sin asignar"}
                      </div>
                      <div className="info-item mb-2">
                        <strong>Creada:</strong>{" "}
                        {formatDate(selectedConversation.dateCreated)}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Messages */}
              <Row>
                <Col xs={12}>
                  <h5 className="mb-3">
                    <i className="fas fa-comments me-2"></i>
                    Mensajes ({conversationMessages.length})
                  </h5>
                  {messagesLoading ? (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p>Cargando mensajes...</p>
                    </div>
                  ) : conversationMessages.length > 0 ? (
                    <div
                      className="messages-container"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {conversationMessages.map((message) => (
                        <div
                          key={message._id}
                          className="message-item mb-3 p-3 border rounded"
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center">
                              <i
                                className={`${getSenderIcon(
                                  message.sender
                                )} me-2`}
                              ></i>
                              <strong>{message.sender}</strong>
                              <Badge color="info" className="ms-2">
                                <i
                                  className={`${getMessageTypeIcon(
                                    message.type
                                  )} me-1`}
                                ></i>
                                {message.type}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {formatDate(message.dateCreated)}
                            </small>
                          </div>
                          <div className="message-content">
                            {message.content?.text && (
                              <p className="mb-2">{message.content.text}</p>
                            )}
                            {message.content?.html && (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeMessageHtml(
                                    message.content.html
                                  ),
                                }}
                              />
                            )}
                            {message.whatsappTemplateName && (
                              <div className="template-info">
                                <Badge color="success">
                                  Template: {message.whatsappTemplateName}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <div className="message-meta">
                            <small className="text-muted">
                              Canal: {message.channel} | Estado:{" "}
                              <Badge
                                color={getStatusBadgeColor(message.status)}
                              >
                                {message.status}
                              </Badge>
                            </small>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">
                      No hay mensajes en esta conversación
                    </p>
                  )}
                  {hasMoreMessages && (
                    <div className="text-center mt-3">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        disabled={loadingMoreMessages}
                        onClick={loadMoreMessages}
                      >
                        {loadingMoreMessages ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Cargando...
                          </>
                        ) : (
                          "Cargar mensajes anteriores"
                        )}
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeConversationModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        toggle={() => setShowConfigModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowConfigModal(false)}>
          <i className="fas fa-cog me-2"></i>
          Configurar Análisis con IA
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="analysisModel">
                    <strong>Modelo de IA</strong>
                  </Label>
                  <Input
                    id="analysisModel"
                    type="select"
                    value={analysisConfig.model}
                    onChange={(e) =>
                      setAnalysisConfig({
                        ...analysisConfig,
                        model: e.target.value,
                      })
                    }
                  >
                    <option value="us.anthropic.claude-haiku-4-5-20251001-v1:0">Claude Haiku 4.5</option>
                    {/* <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option> */}
                  </Input>
                  <small className="text-muted">
                    Selecciona el modelo de IA para el análisis
                  </small>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="analysisPrompt">
                <strong>Prompt para el Análisis</strong>
              </Label>
              <Input
                id="analysisPrompt"
                type="textarea"
                rows="15"
                value={analysisConfig.prompt}
                onChange={(e) =>
                  setAnalysisConfig({
                    ...analysisConfig,
                    prompt: e.target.value,
                  })
                }
                placeholder="Ingresa el prompt que se usará para analizar cada conversación..."
              />
              <small className="text-muted">
                El prompt debe incluir instrucciones sobre el formato de
                respuesta esperado (preferiblemente JSON).
              </small>
            </FormGroup>

            <hr />

            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label>
                  <strong>Columnas de Salida en Excel</strong>
                </Label>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleAddCustomColumn}
                >
                  <i className="fas fa-plus me-1"></i>
                  Agregar Columna Personalizada
                </Button>
              </div>

              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {analysisConfig.outputColumns.map((column, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                  >
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id={`column-${index}`}
                        checked={column.enabled}
                        onChange={() => handleColumnToggle(index)}
                        className="form-check-input"
                      />
                      <Label
                        for={`column-${index}`}
                        className="form-check-label ms-2"
                      >
                        {column.label}{" "}
                        <small className="text-muted">({column.key})</small>
                      </Label>
                    </div>
                    {index >= 12 && ( // Only show remove for custom columns (after default ones)
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveCustomColumn(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowConfigModal(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={startAnalysis}
            disabled={!analysisConfig.prompt.trim()}
          >
            <i className="fas fa-play me-1"></i>
            Iniciar Análisis
          </Button>
        </ModalFooter>
      </Modal>

      {/* Analysis Progress Modal */}
      <Modal
        isOpen={showAnalysisModal}
        toggle={() => {
          if (!isAnalyzing) {
            setShowAnalysisModal(false);
          }
        }}
        size="lg"
      >
        <ModalHeader toggle={() => !isAnalyzing && setShowAnalysisModal(false)}>
          <i className="fas fa-brain me-2"></i>
          Análisis de Conversaciones con IA
        </ModalHeader>
        <ModalBody>
          {isAnalyzing ? (
            <div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>
                    Progreso: {analysisProgress.current} /{" "}
                    {analysisProgress.total}
                  </span>
                  <span>{analysisProgress.percentage || 0}%</span>
                </div>
                <div className="progress" style={{ height: "25px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${analysisProgress.percentage || 0}%` }}
                  >
                    {analysisProgress.percentage || 0}%
                  </div>
                </div>
              </div>
              {batchErrors.length > 0 && (
                <Alert color="warning">
                  <strong>Errores:</strong> {batchErrors.length} conversaciones
                  tuvieron errores
                </Alert>
              )}
              <div className="mt-3">
                <small className="text-muted">
                  Analizando conversaciones... Esto puede tomar varios minutos
                  dependiendo de la cantidad de conversaciones.
                </small>
              </div>
            </div>
          ) : (
            <div>
              <Alert color="success">
                <strong>Análisis completado!</strong>
                <br />
                {batchResults.length} conversaciones analizadas exitosamente
                {batchErrors.length > 0 && (
                  <>
                    <br />
                    {batchErrors.length} conversaciones con errores
                  </>
                )}
              </Alert>
              {batchResults.length > 0 && (
                <Button
                  color="success"
                  onClick={() => exportAnalysisToExcel()}
                  className="mt-3"
                  block
                >
                  <i className="fas fa-file-excel me-1"></i>
                  Exportar Resultados a Excel
                </Button>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {isAnalyzing ? (
            <>
              {isAnalysisPaused ? (
                <Button color="success" onClick={resumeAnalysis}>
                  <i className="fas fa-play me-1"></i>
                  Reanudar
                </Button>
              ) : (
                <Button color="warning" onClick={pauseAnalysis}>
                  <i className="fas fa-pause me-1"></i>
                  Pausar
                </Button>
              )}
              <Button color="danger" onClick={cancelAnalysis}>
                <i className="fas fa-stop me-1"></i>
                Cancelar
              </Button>
            </>
          ) : (
            <Button
              color="secondary"
              onClick={() => setShowAnalysisModal(false)}
            >
              Cerrar
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default connect(select)(Conversations);
